import React, { useEffect, useRef, useState } from "react";
import { api, imageUrl } from "@/lib/api";
import { toast } from "sonner";
import { useColours } from "@/contexts/ColoursContext";
import { X, Plus, Save, Trash2, ArrowUp, ArrowDown, Upload, Image as ImageIcon } from "lucide-react";

/**
 * Manager-only — admin-managed colours (Beige, Black, Royal Blue, etc.)
 * used to group/filter stock items. Optional hex swatch shows a tiny dot.
 */
const ManageColoursDialog = ({ open, onClose }) => {
  const { reload } = useColours();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState("");
  const [newSwatch, setNewSwatch] = useState("");
  const [newSwatchImage, setNewSwatchImage] = useState(null);
  const [newUploading, setNewUploading] = useState(false);
  const newFileInput = useRef(null);
  const fileInputs = useRef({});

  const uploadNew = async (file) => {
    if (!file) return;
    setNewUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setNewSwatchImage(data.path);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setNewUploading(false);
    }
  };

  const uploadFor = async (id, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Immediately persist + reload so the swatch shows everywhere.
      const row = rows.find((r) => r.id === id);
      await api.put(`/colours/${id}`, {
        name: row.name,
        swatch: row.swatch || null,
        swatch_image: data.path,
        sort: row.sort,
      });
      await load();
      await reload();
      toast.success("Swatch image uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const clearImage = async (row) => {
    setBusy(true);
    try {
      await api.put(`/colours/${row.id}`, {
        name: row.name,
        swatch: row.swatch || null,
        swatch_image: "",
        sort: row.sort,
      });
      await load();
      await reload();
    } catch (e) {
      toast.error("Could not clear image");
    } finally {
      setBusy(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/colours");
      setRows(data || []);
    } catch (_) {
      toast.error("Could not load colours");
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  if (!open) return null;

  const create = async () => {
    const name = newName.trim();
    if (!name) return toast.error("Name required");
    setBusy(true);
    try {
      await api.post("/colours", {
        name,
        swatch: newSwatchImage ? null : newSwatch.trim() || null,
        swatch_image: newSwatchImage || null,
      });
      setNewName("");
      setNewSwatch("");
      setNewSwatchImage(null);
      await load();
      await reload();
      toast.success(`Added colour "${name}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add");
    } finally {
      setBusy(false);
    }
  };

  const save = async (row) => {
    setBusy(true);
    try {
      await api.put(`/colours/${row.id}`, {
        name: row.name,
        swatch: row.swatch || null,
        swatch_image: row.swatch_image || null,
        sort: row.sort,
      });
      await reload();
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (
      !window.confirm(
        `Delete colour "${row.name}"? Items using it must be reassigned first.`,
      )
    )
      return;
    setBusy(true);
    try {
      await api.delete(`/colours/${row.id}`);
      await load();
      await reload();
      toast.success("Deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const move = (idx, dir) => {
    const newRows = [...rows];
    const j = idx + dir;
    if (j < 0 || j >= newRows.length) return;
    [newRows[idx], newRows[j]] = [newRows[j], newRows[idx]];
    newRows.forEach((r, i) => (r.sort = i));
    setRows(newRows);
    api.put(`/colours/${newRows[idx].id}`, { sort: idx }).catch(() => {});
    api.put(`/colours/${newRows[j].id}`, { sort: j }).then(reload).catch(() => {});
  };

  const update = (id, patch) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="manage-colours-dialog"
      onClick={onClose}
    >
      <div
        className="nb-card w-[min(680px,94vw)] max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white px-5 py-4 border-b-2 border-black flex items-center justify-between">
          <div>
            <div className="label-eyebrow">Inventory</div>
            <h3 className="font-display text-2xl">Manage colours</h3>
          </div>
          <button
            onClick={onClose}
            data-testid="manage-colours-close"
            className="nb-btn bg-white p-2"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        <div className="p-5 flex flex-col gap-3">
          {rows.length === 0 && (
            <div className="text-sm text-[#607087] italic text-center py-4">
              No colours yet. Add Beige, Black, Royal Blue, Charcoal — whatever you stock.
            </div>
          )}
          {rows.map((row, idx) => (
            <div
              key={row.id}
              data-testid={`colour-row-${row.name}`}
              className="border-2 border-black rounded-xl p-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3"
            >
              <div className="flex gap-1">
                <button
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  className="nb-btn bg-white p-1.5 disabled:opacity-30"
                  aria-label="move up"
                >
                  <ArrowUp size={12} strokeWidth={2.5} />
                </button>
                <button
                  onClick={() => move(idx, 1)}
                  disabled={idx === rows.length - 1}
                  className="nb-btn bg-white p-1.5 disabled:opacity-30"
                  aria-label="move down"
                >
                  <ArrowDown size={12} strokeWidth={2.5} />
                </button>
              </div>
              <input
                data-testid={`colour-name-${row.name}`}
                className="nb-input flex-1"
                value={row.name}
                onChange={(e) => update(row.id, { name: e.target.value })}
              />
              <div className="flex items-center gap-2">
                {row.swatch_image ? (
                  <div
                    className="relative h-10 w-12 border-2 border-black rounded-md overflow-hidden"
                    data-testid={`colour-image-thumb-${row.name}`}
                  >
                    <img
                      src={imageUrl(row.swatch_image)}
                      alt={row.name}
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => clearImage(row)}
                      data-testid={`colour-image-clear-${row.name}`}
                      className="absolute -top-1 -right-1 bg-white border border-black rounded-full p-0.5"
                      aria-label="clear image"
                      title="Remove image"
                    >
                      <X size={10} strokeWidth={2.5} />
                    </button>
                  </div>
                ) : (
                  <input
                    type="color"
                    data-testid={`colour-swatch-${row.name}`}
                    className="h-10 w-12 border-2 border-black rounded-md cursor-pointer p-0"
                    value={row.swatch || "#cccccc"}
                    onChange={(e) => update(row.id, { swatch: e.target.value })}
                    title="Pick swatch"
                  />
                )}
                <input
                  ref={(el) => (fileInputs.current[row.id] = el)}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid={`colour-upload-input-${row.name}`}
                  onChange={(e) => uploadFor(row.id, e.target.files?.[0])}
                />
                <button
                  type="button"
                  data-testid={`colour-upload-btn-${row.name}`}
                  onClick={() => fileInputs.current[row.id]?.click()}
                  className="nb-btn bg-white p-2"
                  aria-label="upload swatch image"
                  title={row.swatch_image ? "Replace image" : "Upload swatch image"}
                >
                  <Upload size={14} strokeWidth={2.5} />
                </button>
                {!row.swatch_image && row.swatch && (
                  <button
                    type="button"
                    onClick={() => update(row.id, { swatch: "" })}
                    className="nb-btn bg-white p-1.5"
                    aria-label="clear swatch"
                    title="Clear hex swatch"
                  >
                    <X size={12} strokeWidth={2.5} />
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  data-testid={`colour-save-${row.name}`}
                  onClick={() => save(row)}
                  disabled={busy}
                  className="nb-btn nb-btn-primary px-3 py-2 text-sm flex items-center gap-1"
                >
                  <Save size={14} strokeWidth={2.5} /> Save
                </button>
                <button
                  data-testid={`colour-delete-${row.name}`}
                  onClick={() => remove(row)}
                  disabled={busy}
                  className="nb-btn nb-btn-danger p-2"
                  aria-label="delete"
                >
                  <Trash2 size={14} strokeWidth={2.5} />
                </button>
              </div>
            </div>
          ))}

          <div className="border-2 border-dashed border-[#001740] rounded-xl p-3 flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-3 bg-[#F4F8FB]">
            <label className="flex-1">
              <div className="label-eyebrow mb-1">New colour</div>
              <input
                data-testid="new-colour-name"
                className="nb-input"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && create()}
                placeholder="e.g. Beige, Black, Royal Blue"
              />
            </label>
            <div className="flex flex-col gap-1">
              <div className="label-eyebrow">Swatch</div>
              <div className="flex items-center gap-2">
                {newSwatchImage ? (
                  <div
                    className="relative h-10 w-12 border-2 border-black rounded-md overflow-hidden"
                    data-testid="new-colour-image-thumb"
                  >
                    <img
                      src={imageUrl(newSwatchImage)}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      data-testid="new-colour-image-clear"
                      onClick={() => setNewSwatchImage(null)}
                      className="absolute -top-1 -right-1 bg-white border border-black rounded-full p-0.5"
                      aria-label="clear image"
                      title="Remove image"
                    >
                      <X size={10} strokeWidth={2.5} />
                    </button>
                  </div>
                ) : (
                  <input
                    type="color"
                    data-testid="new-colour-swatch"
                    className="h-10 w-12 border-2 border-black rounded-md cursor-pointer p-0"
                    value={newSwatch || "#cccccc"}
                    onChange={(e) => setNewSwatch(e.target.value)}
                  />
                )}
                <input
                  ref={newFileInput}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="new-colour-upload-input"
                  onChange={(e) => uploadNew(e.target.files?.[0])}
                />
                <button
                  type="button"
                  data-testid="new-colour-upload-btn"
                  onClick={() => newFileInput.current?.click()}
                  disabled={newUploading}
                  className="nb-btn bg-white p-2"
                  aria-label="upload swatch image"
                  title={newSwatchImage ? "Replace image" : "Upload swatch image"}
                >
                  <Upload size={14} strokeWidth={2.5} />
                </button>
              </div>
            </div>
            <button
              data-testid="new-colour-add"
              onClick={create}
              disabled={busy || newUploading || !newName.trim()}
              className="nb-btn nb-btn-success px-4 py-2 text-sm flex items-center gap-1"
            >
              <Plus size={14} strokeWidth={2.5} /> Add
            </button>
          </div>

          <div className="label-eyebrow text-[#607087] mt-1">
            Renaming a colour cascades to existing items.
            Delete is blocked while items still reference it.
            Upload a real swatch photo for patterned colours like Tiger or Splash —
            it&apos;ll appear on chips, badges and the item form.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageColoursDialog;
